<template>
  <b-modal :title="$t('labels.provider_invoice_diff')"
           dialog-class="modal-xxl"
           :modelValue="provider !== null"
           footer-class="d-flex justify-content-end"
           @update:modelValue="$event === false && (provider = null)">
    <template #default>
      <LoaderWrapper ref="loader" class="h-100">
        <div class="p-3 row pt-0">

          <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
            <div class="col p-0">
              <div class="">
                <DatePicker v-model="filters.fromDate" mode="dateTime" :model-modifiers="{string: true}"  is24hr :masks="{modelValue: 'YYYY-MM-DD HH:mm:ss', inputDateTime24hr: 'YYYY-MM-DD HH:mm:ss'}">
                  <template v-slot="{ inputValue, togglePopover, inputEvents }">
                    <input
                      class="form-control form-control-sm"
                      :placeholder="helper.trans('labels.from_date')"
                      :value="inputValue"
                      @focus="togglePopover"
                      @change="inputEvents.change"
                    />
                  </template>
                </DatePicker>
              </div>
            </div>
          </div>

          <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
            <div class="col p-0">
              <div class="">
                <DatePicker v-model="filters.toDate" mode="dateTime" :model-modifiers="{string: true}"  is24hr :masks="{modelValue: 'YYYY-MM-DD HH:mm:ss', inputDateTime24hr: 'YYYY-MM-DD HH:mm:ss'}">
                  <template v-slot="{ inputValue, togglePopover, inputEvents }">
                    <input
                      class="form-control form-control-sm"
                      :placeholder="helper.trans('labels.to_date')"
                      :value="inputValue"
                      @focus="togglePopover"
                      @change="inputEvents.change"
                    />
                  </template>
                </DatePicker>
              </div>
            </div>
          </div>

          <div class="form-group mb-2 col">
            <div class="col-auto p-0 text-end">
              <button type="button" @click.prevent="() => applyFilters()" class="btn btn-outline-primary btn-sm rounded-pill me-2 text-capitalize-first">
                {{ helper.trans('button.apply') }}
              </button>
            </div>
          </div>
        </div>


        <div class="p-3 pt-0" v-if="provider">
          <div class="overflow-auto">
            <table class="table table-striped border-light align-middle text-left position-relative ">
              <thead>
              <tr>
                <th class="text-capitalize-first">{{ $t('labels.id') }}</th>
                <th class="text-capitalize-first">{{ $t('labels.sum') }}</th>
                <th class="text-capitalize-first">{{ $t('labels.status') }}</th>
                <th class="text-capitalize-first">{{ $t('labels.date') }}</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, index) in paginatedResponse.items" :key="index" class="">
                <td>
                  <span class="me-3">
                    <span v-b-tooltip.hover :title="item.invoiceId">{{ item.invoiceId.substring(0, 8) }}...</span> <copy-button :value="item.invoiceId"></copy-button>
                  </span>
                  <span>
                    <span v-b-tooltip.hover :title="item.providerId">{{ item.providerId.substring(0, 8) }}...</span> <copy-button :value="item.providerId"></copy-button>
                  </span>
                </td>
                <td>
                  {{numberFormatter.formatMoney(item.takerSum)}}
                </td>
                <td>
                  {{ $t(`invoice_status.${item.status}`) }} -  <span v-if="item.providerStatus">{{ $t(`invoice_status.${item.providerStatus}`) }}</span><span v-else>n/a</span>
                </td>
                <td>
                  {{dateHelper.format(item.createdAt)}}
                </td>
              </tr>
              </tbody>
            </table>
            <b-pagination
              v-if="paginatedResponse.totalCount > paginatedResponse.items.length"
              v-model="page"
              :total-rows="paginatedResponse.totalCount"
              :per-page="paginatedResponse.limit"
              :limit="20"
              @update:modelValue="load()"
              :prev-text="helper.trans('button.back')"
              :next-text="helper.trans('button.next')"
              align="start"
              first-number
              hide-goto-end-buttons
              hide-ellipsis
            ></b-pagination>
          </div>
        </div>
      </LoaderWrapper>
    </template>
    <template #footer>
      <button type="button" @click.prevent="() => downloadReport()" class="btn btn-link btn-sm m-0 p-0 text-capitalize-first me-3">
        <i class="fa-solid fa-download"></i> {{ $t('labels.download_report') }}
      </button>

      <button type="button" @click.prevent="provider = null" class="btn btn-outline-dark btn-sm text-capitalize-first">
        {{ helper.trans('button.ok')  }}
      </button>
    </template>
  </b-modal>
</template>

<script lang="ts">

import {defineComponent, PropType, Prop, watch, ref} from 'vue'
import {$t, helper} from "@/service/CommonHelper";
import {reactive, toRefs} from "@vue/reactivity";
import {api} from "@/service/APIClient";
import {dateHelper} from "@/service/DateHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import {BModal, BPagination} from "bootstrap-vue-3";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {DatePicker} from "v-calendar";
import PaginatedResponseDTO from "@/models/PaginatedResponseDTO";
import ProviderRequestDTO from "@/models/ProviderRequestDTO";
import CopyButton from "@/components/CopyButton.vue";
import moment from "moment";
import ProviderInvoiceDiffItemDTO from "@/models/ProviderInvoiceDiffItemDTO";
import ProviderDTO from "@/models/ProviderDTO";

export default defineComponent({
  methods: {$t},
  components: {
    CopyButton,
    BPagination,
    DatePicker,
    LoaderWrapper,
    BModal,
  },
  setup() {
    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const defaultFilters = {fromDate: moment().subtract(1, 'day').format('YYYY-MM-DD 00:00:00'), toDate: null} as any;

    const state = reactive({
      filters: {...defaultFilters} as any,
      provider: null as ProviderDTO|null,
      page: 1,
      limit: 20,
      paginatedResponse: {
        limit: 20,
        offset: 0,
        totalCount: 0,
        items: []
      } as PaginatedResponseDTO<ProviderInvoiceDiffItemDTO>
    });

    const show = (provider: ProviderDTO) => {
      state.filters = {...defaultFilters}
      state.provider = provider
      applyFilters()
    }

    const applyFilters = async () => {
      state.paginatedResponse.items = []
      state.page = 1;
      load()
    }

    const downloadReport = async () => {
      loader.value?.show()

      try {
        const data = await api.export('provider_invoice_diff', {
          providerId: state.provider!.id,
          fromDate: dateHelper.toUTC(state.filters.fromDate),
          toDate: dateHelper.toUTC(state.filters.toDate),
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(new Blob([data]));
        link.href = url
        link.setAttribute('download', `provider_${state.provider!.sysname}_diff.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } finally {
        loader.value?.hide()
      }
    }

    const load = async () => {
      if (!state.provider?.id) {
        return;
      }

      loader.value?.show()
      try {
        const offset = state.limit * (state.page - 1)
        state.paginatedResponse = await api.call.providers.invoiceDiff(state.provider.id, {
          limit: state.limit,
          offset: offset
        }, {
          fromDate: dateHelper.toUTC(state.filters.fromDate),
          toDate: dateHelper.toUTC(state.filters.toDate),
        });
      } finally {
        loader.value?.hide()
      }
    }

    return {
      ...toRefs(state),
      downloadReport,
      show,
      applyFilters,
      load,
      loader,
      dateHelper,
      numberFormatter,
      helper
    }
  }
})
</script>
